import React, { useState, useEffect } from 'react';
import apiService from '../services/apiService';
import LazyImage from '../components/LazyImage';

function Gallery() {
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  const [validItems, setValidItems] = useState([]);

  useEffect(() => {
    loadGallery();
  }, []);

  useEffect(() => {
    // Close modal on escape key
    const handleEscape = (e) => {
      if (e.key === 'Escape' && selectedItem) {
        setSelectedItem(null);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [selectedItem]);

  const loadGallery = () => {
    setLoading(true);
    apiService.getGallery()
      .then(response => {
        // Remove duplicates based on file_path
        const uniqueItems = [];
        const seenPaths = new Set();
        
        response.data.gallery.forEach(item => {
          if (!seenPaths.has(item.file_path)) {
            seenPaths.add(item.file_path);
            uniqueItems.push(item);
          }
        });
        
        setValidItems(uniqueItems); // Start with all items, filter as they fail
        setLoading(false);
      })
      .catch(error => {
        console.error('Error loading gallery:', error);
        setLoading(false);
      });
  };

  const handleItemError = (itemId) => {
    // Silently remove items that fail to load
    setValidItems(prev => prev.filter(item => item.id !== itemId));
  };

  const handleItemClick = (item) => {
    setSelectedItem(item);
  };

  const handleCloseModal = () => {
    setSelectedItem(null);
  };

  const handleModalClick = (e) => {
    // Close modal when clicking outside the content
    if (e.target.classList.contains('gallery-modal-overlay')) {
      setSelectedItem(null);
    }
  };

  return (
    <div className="gallery-page">
      <section className="hero-section" style={{backgroundImage: 'url(/images/gallery-bg.jpg)', height: '60vh'}}>
        <div className="hero-content">
          <h1 className="hero-title">GALLERY</h1>
        </div>
      </section>

      <section className="section section-light">
        <div className="container">
          {loading ? (
            <div style={{textAlign: 'center', padding: '60px', color: '#999'}}>
              <p>Loading gallery...</p>
            </div>
          ) : validItems.length === 0 ? (
            <div style={{textAlign: 'center', padding: '60px', color: '#999'}}>
              <p>No gallery items available.</p>
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
              gap: '20px'
            }}>
              {validItems.map(item => (
                <div 
                  key={item.id} 
                  className="gallery-item-card"
                  style={{
                    position: 'relative', 
                    overflow: 'hidden',
                    borderRadius: '4px',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                    cursor: 'pointer'
                  }}
                  onClick={() => handleItemClick(item)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
                  }}
                >
                  {item.file_type === 'image' ? (
                    <LazyImage 
                      src={item.file_path} 
                      alt={item.title || 'Gallery image'} 
                      style={{
                        width: '100%', 
                        height: '300px', 
                        objectFit: 'cover',
                        display: 'block'
                      }}
                      onError={() => handleItemError(item.id)}
                    />
                  ) : (
                    <video 
                      src={item.file_path} 
                      muted
                      loop
                      autoPlay
                      playsInline
                      style={{
                        width: '100%', 
                        height: '300px', 
                        objectFit: 'cover',
                        display: 'block'
                      }}
                      onLoadedData={(e) => {
                        // Set thumbnail
                        e.target.currentTime = 0.1;
                      }}
                      onError={() => handleItemError(item.id)}
                    />
                  )}
                  
                  {/* Type indicator badge */}
                  <div style={{
                    position: 'absolute',
                    top: '10px',
                    right: '10px',
                    background: item.file_type === 'video' 
                      ? 'rgba(46, 139, 192, 0.9)' 
                      : 'rgba(0, 0, 0, 0.7)',
                    color: '#fff',
                    padding: '6px 12px',
                    borderRadius: '4px',
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    zIndex: 2,
                    pointerEvents: 'none'
                  }}>
                    {item.file_type === 'video' ? (
                      <>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M8 5v14l11-7z" fill="currentColor"/>
                        </svg>
                        Video
                      </>
                    ) : (
                      <>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2" fill="none"/>
                          <circle cx="8.5" cy="8.5" r="1.5" fill="currentColor"/>
                          <path d="M21 15l-5-5L5 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                        Image
                      </>
                    )}
                  </div>
                  
                  {item.title && (
                    <div style={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      background: 'linear-gradient(to top, rgba(0,0,0,0.8), transparent)',
                      color: 'white',
                      padding: '20px 15px 15px',
                      fontSize: '0.9rem',
                      pointerEvents: 'none'
                    }}>
                      {item.title}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Modal/Lightbox */}
      {selectedItem && (
        <div 
          className="gallery-modal-overlay"
          onClick={handleModalClick}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.95)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            padding: '20px',
            cursor: 'pointer'
          }}
        >
          <div 
            className="gallery-modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'relative',
              maxWidth: '95vw',
              maxHeight: '95vh',
              cursor: 'default'
            }}
          >
            <button
              onClick={handleCloseModal}
              style={{
                position: 'absolute',
                top: '-40px',
                right: '0',
                background: 'rgba(255, 255, 255, 0.2)',
                border: 'none',
                color: '#fff',
                fontSize: '32px',
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                lineHeight: 1,
                zIndex: 10001,
                transition: 'background 0.3s'
              }}
              onMouseEnter={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.3)'}
              onMouseLeave={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.2)'}
            >
              ×
            </button>
            
            {selectedItem.file_type === 'image' ? (
              <img 
                src={selectedItem.file_path} 
                alt={selectedItem.title || 'Gallery image'} 
                style={{
                  maxWidth: '100%',
                  maxHeight: '95vh',
                  objectFit: 'contain',
                  display: 'block',
                  borderRadius: '8px'
                }}
              />
            ) : (
              <video 
                src={selectedItem.file_path} 
                controls
                loop
                autoPlay
                style={{
                  maxWidth: '100%',
                  maxHeight: '95vh',
                  display: 'block',
                  borderRadius: '8px'
                }}
              />
            )}
            
            {selectedItem.title && (
              <div style={{
                position: 'absolute',
                bottom: '-50px',
                left: 0,
                right: 0,
                textAlign: 'center',
                color: '#fff',
                fontSize: '1.1rem',
                fontWeight: '500',
                padding: '10px'
              }}>
                {selectedItem.title}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default Gallery;
